import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PersonalizationProvider } from "./contexts/PersonalizationContext";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AnimationProvider } from "./components/animations";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useAccessibility } from "./hooks/useAccessibility";
import { useInactivityLogout } from "./hooks/useInactivityLogout";
import { useThemeApplication } from "./hooks/useThemeApplication";
import { useReferralTracking } from "./hooks/useReferralTracking";
import LoadingSpinner from "./components/LoadingSpinner";
import { allRoutes } from "./routes";
import { UsageWarningBanner } from "./components/usage/UsageWarningBanner";
import { RealtimeToast } from "./components/notifications/RealtimeToast";

// Optimized QueryClient configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - reduce unnecessary refetches
      gcTime: 30 * 60 * 1000, // 30 minutes - keep more in memory
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

function AccessibilityWrapper({ children }: { children: React.ReactNode }) {
  useAccessibility();
  useInactivityLogout(); // Use defaults: 7 days (10080 minutes)
  useThemeApplication();
  useReferralTracking(); // Initialize referral tracking
  return (
    <>
      <UsageWarningBanner />
      <RealtimeToast />
      {children}
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {allRoutes.map(({ path, component: Component, requiresAuth, requiresAdmin }) => (
        <Route
          key={path}
          path={path}
          element={
            requiresAuth ? (
              <ProtectedRoute requireAdmin={requiresAdmin}>
                <Component />
              </ProtectedRoute>
            ) : (
              <Component />
            )
          }
        />
      ))}
    </Routes>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark">
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <PersonalizationProvider>
              <AnimationProvider>
                <AccessibilityWrapper>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <Suspense fallback={
                      <div className="min-h-screen flex items-center justify-center bg-background">
                        <LoadingSpinner />
                      </div>
                    }>
                      <AppRoutes />
                    </Suspense>
                  </TooltipProvider>
                </AccessibilityWrapper>
              </AnimationProvider>
            </PersonalizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
